import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, Trash2, IdCard, User as UserIcon, Ruler } from "lucide-react";
import ImageCropper from "@/components/ImageCropper";

export default function Carteirinha() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let url;
    let cancelled = false;
    api.get("/me/carteirinha.pdf", { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setPdfUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [user?.profile_photo_path, user?.name, user?.child_name, user?.child_condition]);

  const pickFile = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem."); return; }
    setPendingFile(f);
    setCropOpen(true);
  };

  const onCropped = async (blob) => {
    setCropOpen(false); setPendingFile(null);
    if (!blob) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post("/me/profile-photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada.");
      await refresh();
    } catch (_) { toast.error("Falha ao enviar foto."); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remover sua foto de perfil?")) return;
    try { await api.delete("/me/profile-photo"); toast.success("Foto removida."); await refresh(); }
    catch (_) { toast.error("Falha ao remover."); }
  };

  const download = async () => {
    try {
      const res = await api.get("/me/carteirinha.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `carteirinha-instituto-cuidar.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (_) { toast.error("Falha ao gerar PDF."); }
  };

  if (!user) return null;
  const cardCode = `IC-${(user.id || "").slice(-8).toUpperCase()}`;

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
        <div className="container-cuidar py-10">
          <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">Sua carteirinha</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black flex items-center gap-3">
            <IdCard className="h-8 w-8 text-[hsl(var(--primary))]" /> Carteirinha do Instituto Cuidar
          </h1>
          <p className="mt-3 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Identificação de apoio para o dia a dia. Personalize sua foto e baixe em PDF pronto para imprimir no tamanho oficial CR80.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] bg-white/70 border border-[hsl(var(--border))] rounded-full px-3 py-1">
            <Ruler className="h-3.5 w-3.5" /> Padrão ISO/IEC 7810 ID-1 · 85,60 × 53,98 mm
          </div>
        </div>
      </section>

      <section className="container-cuidar py-10 grid gap-10 lg:grid-cols-[1fr_1.2fr]">
        {/* Preview card */}
        <div className="surface p-6 sm:p-8">
          <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Prévia</div>
          <div className="mt-4 relative rounded-[1.2rem] overflow-hidden bg-[hsl(var(--muted))] border border-[hsl(var(--border))]" style={{aspectRatio: "85.6/53.98"}}>
            <div className="absolute inset-x-0 top-0 h-[36%] bg-[hsl(var(--primary))] flex items-center justify-between px-4">
              <img src="/logo-instituto-cuidar.png" alt="Instituto Cuidar" className="h-6 sm:h-8 w-auto object-contain brightness-0 invert" />
              <div className="text-white text-[10px] sm:text-xs font-bold tracking-widest">CARTEIRINHA DE APOIO</div>
            </div>
            <div className="absolute left-4 top-[32%] w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-white border-2 border-white shadow-md" data-testid="carteirinha-photo-preview">
              {user.profile_photo_path ? (
                <img src={`${API}/files/${user.profile_photo_path}`} alt="foto" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[hsl(var(--muted-foreground))]"><UserIcon className="h-8 w-8" /></div>
              )}
            </div>
            <div className="absolute right-4 top-[42%] left-28 sm:left-32">
              <div className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Criança</div>
              <div className="font-heading font-extrabold text-sm sm:text-base leading-tight">{user.child_name || "—"}</div>
              <div className="mt-2 text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Situação neuroatípica</div>
              <div className="text-xs leading-snug line-clamp-2">{user.child_condition || "—"}</div>
            </div>
            <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Responsável</div>
                <div className="text-xs font-bold">{user.name}</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Nº</div>
                <div className="text-xs font-bold text-[hsl(var(--secondary))]">{cardCode}</div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">
            Documento não oficial de apoio à identificação. O PDF traz frente e verso em A4 com marcas de corte no tamanho CR80.
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-6">
          <div className="surface p-6 sm:p-7">
            <div className="font-heading text-lg font-bold">Foto de perfil</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Envie uma foto de rosto da criança ou do responsável. Ao enviar, você pode <b>girar, dar zoom e recortar</b> antes de salvar.
            </p>
            <div className="mt-4 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="carteirinha-photo-input" />
              <Button onClick={pickFile} disabled={uploading} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="carteirinha-photo-upload-btn">
                <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Enviando…" : (user.profile_photo_path ? "Trocar foto" : "Enviar foto")}
              </Button>
              {user.profile_photo_path && (
                <Button variant="outline" onClick={removePhoto} className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10" data-testid="carteirinha-photo-remove-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="surface p-6 sm:p-7">
            <div className="font-heading text-lg font-bold">Baixar PDF</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              O arquivo contém frente e verso da carteirinha em folha A4 no tamanho oficial CR80 (85,60 × 53,98 mm), com marcas de corte.
            </p>
            <Button onClick={download} className="mt-4 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="carteirinha-download-btn">
              <Download className="h-4 w-4 mr-1.5" /> Baixar carteirinha em PDF
            </Button>
          </div>

          {pdfUrl && (
            <div className="surface p-3 h-[560px] flex flex-col overflow-hidden">
              <div className="text-xs text-[hsl(var(--muted-foreground))] px-2 py-1">Prévia impressa (A4)</div>
              <iframe title="Prévia PDF" src={pdfUrl} className="flex-1 w-full rounded-xl" data-testid="carteirinha-pdf-iframe" />
            </div>
          )}

          {!user.child_name && (
            <div className="surface p-5 text-sm text-[hsl(var(--destructive))]">
              Você ainda não preencheu o nome da criança. <Link to="/perfil" className="underline font-semibold">Complete seu perfil</Link> para melhorar sua carteirinha.
            </div>
          )}
        </div>
      </section>

      <ImageCropper
        open={cropOpen}
        file={pendingFile}
        onCancel={() => { setCropOpen(false); setPendingFile(null); }}
        onCropped={onCropped}
        title="Ajustar foto da carteirinha"
      />
    </div>
  );
}
